// stores/driverScoreConfigStore.js
'use client';

import { useMemo } from 'react';
import useSWR, { mutate } from 'swr';
import { fleetApi } from '../api/axiosClient';
import FilterBuilder from '../utils/filterBuilder';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcherPost = async ([url, payloadStr]) => {
  try {
    const payload = typeof payloadStr === 'string' ? JSON.parse(payloadStr) : payloadStr;
    const response = await fleetApi.post(url + getTzParam(), payload);
    return response?.data ?? response;
  } catch (err) {
    console.error('API fetcher error:', { url, payloadStr, status: err?.response?.status, data: err?.response?.data });
    throw err;
  }
};

export const revalidateDriverScoreConfigList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/driver-score-config/search-detail');
};

export const useDriverScoreConfigList = (
  category = null,
  page = 0,
  rowsPerPage = 10,
  searchKey = 'name',
  searchValue = '',
  companyId = ''
) => {
  const start = page * rowsPerPage;

  const payload = useMemo(() => {
    const fb = new FilterBuilder().setPaging(start, rowsPerPage).setOrder(['id'], false);

    if (category) {
      fb.equal('category', category);
    }

    if (searchValue && searchValue.trim() !== '') {
      fb.like(searchKey || 'name', searchValue, 'AND');
    }

    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }

    return fb.build();
  }, [category, start, rowsPerPage, searchKey, searchValue, companyId]);

  const key = useMemo(() => ['/driver-score-config/search-detail', JSON.stringify(payload)], [payload]);

  const {
    data,
    error,
    isLoading,
    mutate: revalidate
  } = useSWR(key, fetcherPost, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
  const total = data?.totalRecord ?? data?.totalRecords ?? data?.recordsTotal ?? data?.dataSize ?? rows.length;

  return {
    data: rows,
    totalRecord: total,
    isLoading,
    isError: error,
    revalidate
  };
};

export const createDriverScoreConfig = async (payload) => {
  try {
    console.log('payload', payload);
    const response = await fleetApi.post(`/driver-score-config/new${getTzParam()}`, payload);
    revalidateDriverScoreConfigList();
    return response.data;
  } catch (error) {
    console.error('Error creating driver score config:', error);
    throw error;
  }
};

export const updateDriverScoreConfig = async (id, payload) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.put(`/driver-score-config/edit/${id}${getTzParam()}`, payload);
    revalidateDriverScoreConfigList();
    return response.data;
  } catch (error) {
    console.error('Error updating driver score config:', error);
    throw error;
  }
};

export const deleteDriverScoreConfig = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/driver-score-config/delete/${id}${getTzParam()}`);
    revalidateDriverScoreConfigList();
    return response.data;
  } catch (error) {
    console.error('Error deleting driver score config:', error);
    throw error;
  }
};

export const getDriverScoreConfigById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.get(`/driver-score-config/${id}${getTzParam()}`);
    return response.data?.data ?? response.data ?? response;
  } catch (error) {
    console.error('Error fetching driver score config by id:', error);
    throw error;
  }
};
